import { useLocation, useNavigate } from 'react-router-dom'
import { visibleShortcuts, badgeLabel, shortcutTitle } from '../lib/headerShortcuts'

/**
 * The shortcut row in the header, beside Messages and notifications.
 *
 * Same 32px button and same active treatment as the message icon it sits next
 * to, so the row reads as one set rather than as things bolted on at different
 * times.
 */

const ICONS = {
  newProposal: (
    <>
      <path d="M4 1.8h5.5L12 4.3v9.9H4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8 6.5v4.5M5.8 8.8h4.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  newProject: (
    <>
      <path d="M2 4.5h4l1.5 1.5H14v6.5H2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8 7.5v3.6M6.2 9.3h3.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  networking: (
    <>
      <circle cx="6.5" cy="5.5" r="2.6" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 13.5c0-2.5 2-4.3 4.5-4.3s4.5 1.8 4.5 4.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M12.5 4v4M10.5 6h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  upload: (
    <>
      <path d="M8 10.5V2.5M5 5.5l3-3 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.5 10v3.5h11V10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  calendar: (
    <>
      <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 6.5h12M5.5 1.8v2.4M10.5 1.8v2.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>
  ),
  // A circled check rather than a bare one: the badge sits over the top-right
  // corner, which is exactly where a plain checkmark's long arm ends — it was
  // being clipped into an unreadable "v".
  approvals: (
    <>
      <circle cx="8" cy="8" r="5.6" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.4 8.1l1.9 1.9 3.4-3.9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  invoices: (
    <>
      <path d="M3.5 1.5h9v13l-2-1.3-1.5 1.3-1.5-1.3-1.5 1.3-2-1.3z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M6 5.5h4M6 8.5h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </>
  ),
  meetings: (
    <>
      <path d="M8 2.2a2 2 0 012 2v3.2a2 2 0 11-4 0V4.2a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M4 7.6a4 4 0 008 0M8 11.6v2.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>
  ),
  files: (
    <>
      <path d="M2.5 4.2A1.2 1.2 0 013.7 3h2.4l1.3 1.6h4.9a1.2 1.2 0 011.2 1.2v6A1.2 1.2 0 0112.3 13H3.7a1.2 1.2 0 01-1.2-1.2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </>
  ),
}

export default function HeaderShortcuts({ isAdmin = false, counts = {}, sectionCounts, loaded = true }) {
  const navigate = useNavigate()
  const location = useLocation()
  const shortcuts = visibleShortcuts({ isAdmin, counts, sectionCounts, loaded })
  if (!shortcuts.length) return null

  return (
    <>
      {shortcuts.map(s => {
        const active = location.pathname + location.search === s.path
        const badge = badgeLabel(s.count)
        return (
          <button
            key={s.key}
            onClick={() => navigate(s.path)}
            title={shortcutTitle(s)}
            aria-label={shortcutTitle(s)}
            aria-current={active ? 'page' : undefined}
            className="tb-icon"
            style={{
              position: 'relative', width: 36, height: 36, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: active ? 'var(--off)' : 'none',
              border: `1px solid ${active ? 'var(--bdr)' : 'transparent'}`,
              cursor: 'pointer', padding: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">{ICONS[s.key]}</svg>
            {badge && (
              <span style={{
                position: 'absolute', top: 3, right: 3, minWidth: 14, height: 14, borderRadius: 7,
                background: 'var(--red)',
                // The ring separates the badge from whatever is behind it, so it
                // has to be the surface colour rather than white — white leaves a
                // bright halo in dark mode.
                border: '1.5px solid var(--card)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--fu)', fontSize: 8, fontWeight: 700, color: '#fff',
                lineHeight: 1, padding: '0 3px',
              }}>{badge}</span>
            )}
          </button>
        )
      })}
    </>
  )
}
